import { useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import Text from "@CubeComponents/html/Text/Text";
import { useTranslation } from "@i18n";
import PaperSizeSelector, { DEFAULT_PAPER_SIZE, type PaperSize } from "./PaperSizeSelector";

interface ExportPdfOptionsModalProps {
  isVisible: boolean;
  /** Fired with the chosen paper size when the user confirms. */
  onExport: (paperSize: PaperSize) => void;
  onCancel: () => void;
}

/**
 * Minimal PDF-export options dialog (paper size + Export/Cancel) for export flows that have no
 * options modal of their own. Modules with richer export dialogs (e.g. Forms) embed
 * PaperSizeSelector in their own modal instead.
 */
function ExportPdfOptionsModal({ isVisible, onExport, onCancel }: ExportPdfOptionsModalProps) {
  const { t } = useTranslation();
  const [paperSize, setPaperSize] = useState<PaperSize>(DEFAULT_PAPER_SIZE);

  const handleExport = () => {
    onExport(paperSize);
    setPaperSize(DEFAULT_PAPER_SIZE);
  };

  const handleCancel = () => {
    setPaperSize(DEFAULT_PAPER_SIZE);
    onCancel();
  };

  return (
    <ModalStateDialogComponent
      width="40%"
      headerTitle={
        <div className="flex justify-between w-full items-center">
          <Text>{t("Export PDF")}</Text>
        </div>
      }
      modelContent={<PaperSizeSelector value={paperSize} onChange={setPaperSize} />}
      footerContent={
        <div className="flex gap-4">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleExport}>
            <Text>{t("Export")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleCancel}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      onCancel={handleCancel}
      isVisible={isVisible}
    />
  );
}

export default ExportPdfOptionsModal;
